import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import ActivityImportModal from "./ActivityImportModal";
import ManualActivityForm from "./ManualActivityForm";
import RouteMap from "./RouteMap";
import MapModal from "./MapModal";
import "./Activities.css";
import { formatActivityLocation } from "../utils/activityLocation";
import { useDistanceUnit } from "../contexts/DistanceUnitContext";
import { parseActivityDate } from "../utils/dateUtils";

// Import header assets
import iconMenu from '../assets/icon-menu.svg';
import iconSearch from '../assets/icon-search.svg';
import iconFilter from '../assets/icon-filter.svg';
import iconRunHeart from '../assets/icon-run-heart.svg';
import NotificationBell from './NotificationBell';
import { useNotifications } from "../contexts/NotificationContext";
import ProfilePicture from './ProfilePicture';

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activeTab, setActiveTab] = useState("list");
  const [searchTerm, setSearchTerm] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("Last month");
  const [showImportModal, setShowImportModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [statusMessage, setStatusMessage] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [loadingSplits, setLoadingSplits] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const { refreshNotifications } = useNotifications();
  const { unit, convertDistance, getUnitLabel } = useDistanceUnit();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/');
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    if (storedUser.email) {
      setUserEmail(storedUser.email);
    }
  }, []);

  const fetchActivities = useCallback(
    async (showSpinner = false) => {
      if (!userEmail) return;
      if (showSpinner) {
        setLoading(true);
      }
      try {
        const [activitiesResponse, profileResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/activities`, {
            params: { email: userEmail },
            withCredentials: true
          }),
          axios.get(`${API_BASE_URL}/profile`, {
            params: { email: userEmail },
            withCredentials: true
          }).catch((error) => {
            console.error('Error fetching profile:', error);
            return { data: { profile: null } };
          })
        ]);

        const normalized = (activitiesResponse.data.activities || []).map((activity) => {
          const canDelete =
            typeof activity.can_delete === "boolean"
              ? activity.can_delete
              : activity.is_manual ||
                ["manual", "gpx", "tcx"].includes(
                  (activity.source_type || "").toLowerCase()
                );
          return { ...activity, can_delete: canDelete };
        });
        setActivities(normalized);
        setProfileData(profileResponse.data.profile);

        // Check if we have a specific activity to select from navigation state
        const selectedActivityId = location.state?.selectedActivityId;
        if (selectedActivityId) {
          const activityToSelect = normalized.find(a => a.id === selectedActivityId);
          if (activityToSelect) {
            setSelectedActivity(activityToSelect);
          } else if (normalized.length > 0) {
            setSelectedActivity(normalized[0]);
          } else {
            setSelectedActivity(null);
          }
        } else if (normalized.length > 0) {
          setSelectedActivity(normalized[0]);
        } else {
          setSelectedActivity(null);
        }
        await refreshNotifications();
      } catch (error) {
        console.error("Error fetching activities:", error);
        setStatusMessage({
          type: "error",
          text: "Unable to load activities. Please try again."
        });
      } finally {
        setLoading(false);
      }
    },
    [userEmail, refreshNotifications, location]
  );

  useEffect(() => {
    if (!userEmail) return;
    fetchActivities(true);
  }, [fetchActivities, userEmail]);

  useEffect(() => {
    if (!statusMessage) return;
    const timeout = setTimeout(() => setStatusMessage(null), 4000);
    return () => clearTimeout(timeout);
  }, [statusMessage]);

  const handleImportComplete = async (message = "Run imported successfully.") => {
    await fetchActivities();
    setShowImportModal(false);
    setStatusMessage({ type: "success", text: message });
  };

  const handleManualComplete = async (message = "Run saved successfully.") => {
    await fetchActivities();
    setShowManualModal(false);
    setStatusMessage({ type: "success", text: message });
  };

  const handleModalError = (text) => {
    setStatusMessage({ type: "error", text });
  };

  const canDeleteSelected =
    selectedActivity && (selectedActivity.can_delete || false);

  const selectedLocation = formatActivityLocation(selectedActivity);

  const handleDeleteSelected = async () => {
    if (!selectedActivity || !canDeleteSelected || !userEmail) {
      return;
    }
    const confirmDelete = window.confirm(
      `Delete "${selectedActivity.name || "this run"}"? This cannot be undone.`
    );
    if (!confirmDelete) {
      return;
    }
    try {
      setIsDeleting(true);
      await axios.delete(
        `${API_BASE_URL}/activities/${selectedActivity.id}`,
        {
          params: { email: userEmail },
          withCredentials: true
        }
      );
      setStatusMessage({
        type: "success",
        text: "Activity deleted."
      });
      await fetchActivities(true);
    } catch (error) {
      console.error("Failed to delete activity", error);
      const message =
        error.response?.data?.messages?.[0] ||
        error.response?.data?.error ||
        "Unable to delete activity.";
      setStatusMessage({ type: "error", text: message });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFetchSplits = async (activityId) => {
    if (!userEmail || loadingSplits) {
      return;
    }
    try {
      setLoadingSplits(true);
      const response = await axios.post(
        `${API_BASE_URL}/activities/${activityId}/fetch-details`,
        {},
        {
          params: { email: userEmail },
          withCredentials: true
        }
      );

      // Update the activity in the list with the new data
      const updatedActivity = response.data.activity;
      console.log("Fetched activity data:", updatedActivity);
      console.log("Splits metric:", updatedActivity.splits_metric);
      console.log("Splits count:", updatedActivity.splits_metric?.length);

      setActivities(prevActivities =>
        prevActivities.map(a =>
          a.id === activityId ? { ...a, ...updatedActivity } : a
        )
      );

      // Update selected activity if it's the one we just fetched
      if (selectedActivity && selectedActivity.id === activityId) {
        const newSelectedActivity = { ...selectedActivity, ...updatedActivity };
        console.log("Updated selected activity:", newSelectedActivity);
        console.log("Selected activity splits_metric:", newSelectedActivity.splits_metric);
        setSelectedActivity(newSelectedActivity);
      }

      setStatusMessage({
        type: "success",
        text: "Pace splits loaded successfully"
      });
    } catch (error) {
      console.error("Failed to fetch splits", error);
      const message =
        error.response?.data?.error ||
        "Unable to load pace splits. Please try again.";
      setStatusMessage({ type: "error", text: message });
    } finally {
      setLoadingSplits(false);
    }
  };

  // Filter activities based on search term and selected filter
  const filteredActivities = activities.filter((activity) => {
    // First apply search filter
    const matchesSearch = activity.name?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;

    // Then apply time/type filter
    const activityDate = parseActivityDate(activity);
    const now = new Date();
    if (!activityDate) return false;

    switch (selectedFilter) {
      case "All Activities":
        return true;

      case "Last week":
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return activityDate >= weekAgo;

      case "Last month":
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return activityDate >= monthAgo;

      case "Races":
        return activity.workout_type === 3; // workout_type 3 is race

      case "Favorites":
        return activity.kudos_count > 5 || activity.achievement_count > 0; // Example criteria for favorites

      default:
        return true;
    }
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedActivities = filteredActivities.slice(startIndex, endIndex);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedFilter]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Format distance
  const formatDistance = (meters) => {
    if (!meters) return `0 ${getUnitLabel()}`;
    return `${convertDistance(meters)} ${getUnitLabel()}`;
  };

  // Calculate pace (min/km or min/mi)
  const formatPace = (meters, seconds) => {
    if (!meters || !seconds) return `0:00/${getUnitLabel()}`;
    const distance = unit === 'miles' ? meters / 1609.34 : meters / 1000;
    const minutes = seconds / 60;
    const paceMinutes = minutes / distance;
    const mins = Math.floor(paceMinutes);
    const secs = Math.round((paceMinutes - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, '0')}/${getUnitLabel()}`;
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Name', 'Location', `Distance (${getUnitLabel()})`, 'Duration', 'Pace', 'Avg Heart Rate (bpm)', 'Calories', 'Elevation Gain (m)'];

    const rows = filteredActivities.map(activity => {
      const location = formatActivityLocation(activity) || '';
      const distance = activity.distance ? convertDistance(activity.distance) : '0';
      const duration = formatDuration(activity.moving_time);
      const pace = formatPace(activity.distance, activity.moving_time);
      const heartRate = activity.average_heartrate ? Math.round(activity.average_heartrate) : '--';
      const calories = activity.calories || Math.round((activity.distance / 1000) * 55);
      const elevation = activity.total_elevation_gain || 0;

      return [
        formatDate(activity.start_date),
        activity.name || 'Run',
        location,
        distance,
        duration,
        pace,
        heartRate,
        calories,
        elevation
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `activities_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  if (loading) {
    return (
      <div className="activities-page">
        <Sidebar />
        <div className="activities-loading">Loading activities...</div>
      </div>
    );
  }

  return (
    <div className="activities-page">
      <Sidebar isOpen={sidebarOpen} />

      {/* Main Container */}
      <div className={`activities-main-container ${sidebarOpen ? '' : 'sidebar-closed'}`}>
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <button className="icon-button" onClick={toggleSidebar}>
              <img src={iconMenu} alt="Menu" />
            </button>
            <h1 className="header-title">Runstreak Dashboard</h1>
          </div>
          <div className="header-right">
            <div className="search-container">
              <img src={iconSearch} alt="Search" className="search-icon" />
              <input
                type="text"
                placeholder="Search activities..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <NotificationBell />
            <button className="logout-button" onClick={handleLogout}>Logout</button>
            <ProfilePicture
              profilePictureUrl={profileData?.profile_picture_url}
              firstName={profileData?.first_name}
              lastName={profileData?.last_name}
              email={userEmail}
              size="small"
              showInitials={true}
            />
          </div>
        </header>

        <div className="activities-content">
          {/* Page Header */}
          <div className="activities-page-header">
            <h1 className="activities-title">Activities</h1>
            <div className="activities-action-group">
              <button
                className="activities-import-button"
                onClick={() => setShowImportModal(true)}
              >
                Import GPX/TCX
              </button>
              <button
                className="add-manual-entry-button"
                onClick={() => setShowManualModal(true)}
              >
                + Add Manual Entry
              </button>
            </div>
          </div>

          {statusMessage && (
            <div className={`activity-status-banner ${statusMessage.type}`}>
              {statusMessage.text}
            </div>
          )}

        {/* Search and Filter Section */}
        <div className="activities-search-section">
          <div className="activities-local-search-container">
            <svg className="activities-local-search-icon" viewBox="0 0 15 15" fill="none">
              <path d="M10.5 6.5a4 4 0 11-8 0 4 4 0 018 0zM14 14l-4-4" stroke="#717182" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              className="activities-local-search-input"
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="activities-filter-dropdown">
            <button
              className="activities-filter-button"
              onClick={() => setFilterOpen(!filterOpen)}
            >
              <img src={iconFilter} alt="Filter" width="16" height="16" />
              <span>{selectedFilter}</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {filterOpen && (
              <div className="activities-filter-menu">
                <button
                  className={`filter-menu-item ${selectedFilter === "All Activities" ? "active" : ""}`}
                  onClick={() => { setSelectedFilter("All Activities"); setFilterOpen(false); }}
                >
                  All Activities
                  {selectedFilter === "All Activities" && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8l3 3 7-7" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
                <button
                  className={`filter-menu-item ${selectedFilter === "Last week" ? "active" : ""}`}
                  onClick={() => { setSelectedFilter("Last week"); setFilterOpen(false); }}
                >
                  Last week
                  {selectedFilter === "Last week" && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8l3 3 7-7" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
                <button
                  className={`filter-menu-item ${selectedFilter === "Last month" ? "active" : ""}`}
                  onClick={() => { setSelectedFilter("Last month"); setFilterOpen(false); }}
                >
                  Last month
                  {selectedFilter === "Last month" && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8l3 3 7-7" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
                <button
                  className={`filter-menu-item ${selectedFilter === "Races" ? "active" : ""}`}
                  onClick={() => { setSelectedFilter("Races"); setFilterOpen(false); }}
                >
                  Races
                  {selectedFilter === "Races" && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8l3 3 7-7" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
                <button
                  className={`filter-menu-item ${selectedFilter === "Favorites" ? "active" : ""}`}
                  onClick={() => { setSelectedFilter("Favorites"); setFilterOpen(false); }}
                >
                  Favorites
                  {selectedFilter === "Favorites" && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8l3 3 7-7" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="activities-main-grid">
          {/* Activities List Card */}
          <div className="activities-card">
            <div className="activities-card-header">
              <h2 className="activities-card-title">Recent Activities</h2>
              <p className="activities-card-description">Your running history</p>
            </div>

            {/* Tabs */}
            <div className="activities-tabs">
              <button
                className={`activities-tab-button ${activeTab === "list" ? "active" : ""}`}
                onClick={() => setActiveTab("list")}
              >
                List View
              </button>
              <button
                className={`activities-tab-button ${activeTab === "table" ? "active" : ""}`}
                onClick={() => setActiveTab("table")}
              >
                Table View
              </button>
            </div>

            {/* Activities List */}
            {activeTab === "list" ? (
              <>
              <div className="activities-list">
                {filteredActivities.length === 0 ? (
                  <div className="activities-empty">
                    <p>No activities found. Please sync your Strava activities first.</p>
                  </div>
                ) : (
                  paginatedActivities.map((activity, index) => {
                    const locationLabel = formatActivityLocation(activity);
                    const globalIndex = startIndex + index;
                    return (
                    <div
                      key={activity.id || index}
                      className={`activity-list-item ${selectedActivity?.id === activity.id ? "selected" : ""}`}
                      onClick={() => setSelectedActivity(activity)}
                    >
                      <div className="activity-list-item-left">
                        <div className="activity-icon-container">
                          <img src={iconRunHeart} alt="Run activity" />
                        </div>
                        <div className="activity-info">
                          <div className="activity-info-top">
                            <h3 className="activity-name">{activity.name || "Run"}</h3>
                            {globalIndex < 2 && (
                              <svg className="activity-star" viewBox="0 0 16 16" fill="#FFA500">
                                <path d="M8 1l2 5h5l-4 3.5 1.5 5L8 11l-4.5 3.5L5 9 1 6h5l2-5z"/>
                              </svg>
                            )}
                            {activity.workout_type === 3 && (
                              <div className="activity-badge">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                  <path d="M6 1l1.5 3h3l-2.5 2 1 3L6 7.5 3 9l1-3-2.5-2h3L6 1z" fill="#894b00"/>
                                </svg>
                                <span>Race</span>
                              </div>
                            )}
                          </div>
                          <div className="activity-meta">
                            <div className="activity-meta-item">
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <rect x="2" y="2" width="8" height="8" rx="1" stroke="#717182" strokeWidth="1"/>
                                <path d="M4 1v2M8 1v2M2 5h8" stroke="#717182" strokeWidth="1"/>
                              </svg>
                              <span>{formatDate(activity.start_date)}</span>
                            </div>
                            {locationLabel && (
                              <div className="activity-meta-item">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                  <path d="M6 1C4 1 2.5 2.5 2.5 4.5c0 3 3.5 6.5 3.5 6.5s3.5-3.5 3.5-6.5C9.5 2.5 8 1 6 1z" stroke="#717182" strokeWidth="1"/>
                                  <circle cx="6" cy="4.5" r="1" fill="#717182"/>
                                </svg>
                                <span>{locationLabel}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="activity-stats">
                        <p className="activity-distance">{formatDistance(activity.distance)}</p>
                        <p className="activity-duration">{formatDuration(activity.moving_time)}</p>
                        <p className="activity-pace">{formatPace(activity.distance, activity.moving_time)}</p>
                      </div>
                    </div>
                  );
                })
                )}
              </div>

              {/* Pagination Controls */}
              {filteredActivities.length > 0 && (
                <div className="activities-pagination">
                  <div className="pagination-info">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredActivities.length)} of {filteredActivities.length} activities
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-button"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </button>
                    <button
                      className="pagination-button"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <div className="pagination-pages">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      className="pagination-button"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                    <button
                      className="pagination-button"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      Last
                    </button>
                  </div>
                  <div className="pagination-per-page">
                    <label>Per page:</label>
                    <select value={itemsPerPage} onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
              )}
              </>
            ) : (
              <>
              <div className="activities-table-container">
                <div className="activities-table-header">
                  <button className="activities-export-button" onClick={exportToCSV}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 1v10M4 7l4 4 4-4M1 15h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Export CSV
                  </button>
                </div>
                {filteredActivities.length === 0 ? (
                  <div className="activities-empty">
                    <p>No activities found. Please sync your Strava activities first.</p>
                  </div>
                ) : (
                  <div className="activities-table-wrapper">
                    <table className="activities-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Name</th>
                          <th>Location</th>
                          <th>Distance</th>
                          <th>Duration</th>
                          <th>Pace</th>
                          <th>HR</th>
                          <th>Calories</th>
                          <th>Elev.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedActivities.map((activity, index) => {
                          const locationLabel = formatActivityLocation(activity) || '';
                          const globalIndex = startIndex + index;
                          return (
                            <tr
                              key={activity.id || index}
                              className={selectedActivity?.id === activity.id ? "selected" : ""}
                              onClick={() => setSelectedActivity(activity)}
                            >
                              <td>{formatDate(activity.start_date)}</td>
                              <td>
                                <div className="table-name-cell">
                                  <span>{activity.name || "Run"}</span>
                                  {globalIndex < 2 && (
                                    <svg className="activity-star-small" viewBox="0 0 12 12" fill="#FFA500">
                                      <path d="M6 1l1.5 3h3l-2.5 2 1 3L6 7.5 3 9l1-3-2.5-2h3L6 1z"/>
                                    </svg>
                                  )}
                                  {activity.workout_type === 3 && (
                                    <svg className="activity-race-badge" width="12" height="12" viewBox="0 0 12 12" fill="#894b00">
                                      <path d="M6 1l1.5 3h3l-2.5 2 1 3L6 7.5 3 9l1-3-2.5-2h3L6 1z"/>
                                    </svg>
                                  )}
                                </div>
                              </td>
                              <td className="table-location">{locationLabel}</td>
                              <td className="table-number">{convertDistance(activity.distance)} {getUnitLabel()}</td>
                              <td>{formatDuration(activity.moving_time)}</td>
                              <td className="table-pace">{formatPace(activity.distance, activity.moving_time)}</td>
                              <td className="table-number">
                                {activity.average_heartrate ? Math.round(activity.average_heartrate) : '--'}
                              </td>
                              <td className="table-number">
                                {activity.calories || Math.round((activity.distance / 1000) * 55)}
                              </td>
                              <td className="table-number">+{activity.total_elevation_gain || 0}m</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              {filteredActivities.length > 0 && (
                <div className="activities-pagination">
                  <div className="pagination-info">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredActivities.length)} of {filteredActivities.length} activities
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-button"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </button>
                    <button
                      className="pagination-button"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <div className="pagination-pages">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      className="pagination-button"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                    <button
                      className="pagination-button"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      Last
                    </button>
                  </div>
                  <div className="pagination-per-page">
                    <label>Per page:</label>
                    <select value={itemsPerPage} onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
              )}
              </>
            )}
          </div>

          {/* Activity Detail Panel */}
          {selectedActivity && (
            <div className="activity-detail-panel">
              {/* Detail Stats Card */}
              <div className="activity-detail-card">
                <div className="activity-detail-header">
                  <div>
                    <div className="activity-detail-title-row">
                      <h3 className="activity-detail-title">{selectedActivity.name || "Run"}</h3>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="#FFA500">
                        <path d="M8 1l2 4h4l-3 3 1 4-4-2-4 2 1-4-3-3h4l2-4z"/>
                      </svg>
                    </div>
                    <p className="activity-detail-subtitle">
                      {formatDate(selectedActivity.start_date)}
                      {selectedLocation && ` • ${selectedLocation}`}
                    </p>
                  </div>
                  {canDeleteSelected && (
                    <button
                      type="button"
                      className="activity-delete-button"
                      onClick={handleDeleteSelected}
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Deleting…" : "Delete"}
                    </button>
                  )}
                </div>

                <div className="activity-detail-stats-grid">
                  <div className="activity-detail-stat">
                    <p className="activity-detail-stat-label">Distance</p>
                    <p className="activity-detail-stat-value">{formatDistance(selectedActivity.distance)}</p>
                  </div>
                  <div className="activity-detail-stat">
                    <p className="activity-detail-stat-label">Duration</p>
                    <p className="activity-detail-stat-value">{formatDuration(selectedActivity.moving_time)}</p>
                  </div>
                  <div className="activity-detail-stat">
                    <p className="activity-detail-stat-label">Avg Pace</p>
                    <p className="activity-detail-stat-value teal">
                      {formatPace(selectedActivity.distance, selectedActivity.moving_time)}
                    </p>
                  </div>
                  <div className="activity-detail-stat">
                    <p className="activity-detail-stat-label">Avg Heart Rate</p>
                    <p className="activity-detail-stat-value">
                      {selectedActivity.average_heartrate ? `${Math.round(selectedActivity.average_heartrate)} bpm` : '--'}
                    </p>
                  </div>
                  <div className="activity-detail-stat">
                    <p className="activity-detail-stat-label">Calories</p>
                    <p className="activity-detail-stat-value">
                      {selectedActivity.calories || Math.round((selectedActivity.distance / 1000) * 55)}
                    </p>
                  </div>
                  <div className="activity-detail-stat">
                    <p className="activity-detail-stat-label">Elevation Gain</p>
                    <p className="activity-detail-stat-value">
                      +{selectedActivity.total_elevation_gain || 0}m
                    </p>
                  </div>
                </div>

                {/* Weather Section */}
                {(() => {
                  const temperaturePreference = (localStorage.getItem('temperatureUnit') || 'c').toLowerCase();
                  const formatTemperature = (value) => {
                    if (value === null || value === undefined) return null;
                    if (temperaturePreference === 'f') {
                      return `${Math.round((value * 9) / 5 + 32)}°F`;
                    }
                    return `${Math.round(value)}°C`;
                  };

                  const hasWeather = selectedActivity.temperature !== null && selectedActivity.temperature !== undefined;

                  if (!hasWeather) return null;

                  return (
                    <div style={{
                      marginTop: '16px',
                      padding: '12px',
                      background: '#eef2ff',
                      borderRadius: '8px',
                      border: '1px solid #c7d2fe'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#4338ca',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        {selectedActivity.weather_icon && selectedActivity.weather_icon.trim() && (
                          <img
                            src={`https://openweathermap.org/img/wn/${selectedActivity.weather_icon}@2x.png`}
                            alt="weather"
                            style={{ width: '32px', height: '32px' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span>⛅ Weather at start</span>
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                        gap: '12px',
                        fontSize: '13px'
                      }}>
                        {formatTemperature(selectedActivity.temperature) && (
                          <div>
                            <div style={{ color: '#6b7280' }}>Temperature</div>
                            <div style={{ fontWeight: '600', color: '#111827' }}>
                              {formatTemperature(selectedActivity.temperature)}
                            </div>
                          </div>
                        )}
                        {formatTemperature(selectedActivity.feels_like) && (
                          <div>
                            <div style={{ color: '#6b7280' }}>Feels like</div>
                            <div style={{ fontWeight: '600', color: '#111827' }}>
                              {formatTemperature(selectedActivity.feels_like)}
                            </div>
                          </div>
                        )}
                        {selectedActivity.weather_condition && (
                          <div>
                            <div style={{ color: '#6b7280' }}>Condition</div>
                            <div style={{ fontWeight: '600', color: '#111827' }}>
                              {selectedActivity.weather_condition}
                            </div>
                          </div>
                        )}
                        {selectedActivity.humidity !== null && selectedActivity.humidity !== undefined && (
                          <div>
                            <div style={{ color: '#6b7280' }}>Humidity</div>
                            <div style={{ fontWeight: '600', color: '#111827' }}>
                              {selectedActivity.humidity}%
                            </div>
                          </div>
                        )}
                        {selectedActivity.wind_speed !== null && selectedActivity.wind_speed !== undefined && (
                          <div>
                            <div style={{ color: '#6b7280' }}>Wind</div>
                            <div style={{ fontWeight: '600', color: '#111827' }}>
                              {(selectedActivity.wind_speed * 3.6).toFixed(1)} km/h
                            </div>
                          </div>
                        )}
                        {selectedActivity.precipitation !== null && selectedActivity.precipitation !== undefined && selectedActivity.precipitation > 0 && (
                          <div>
                            <div style={{ color: '#6b7280' }}>Precipitation</div>
                            <div style={{ fontWeight: '600', color: '#111827' }}>
                              {selectedActivity.precipitation.toFixed(1)} mm
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Pace Chart Card */}
              <div className="chart-card">
                <div className="activity-detail-header">
                  <h3 className="activity-detail-title">Pace Chart</h3>
                  <p className="activity-detail-subtitle">Pace per kilometer</p>
                </div>
                <div className="chart-placeholder">
                  {(() => {
                    // Check if we have real splits data
                    console.log("Rendering pace chart. Splits data:", selectedActivity.splits_metric);
                    if (!selectedActivity.splits_metric || selectedActivity.splits_metric.length === 0) {
                      // Calculate overall average pace from distance and moving_time
                      if (selectedActivity.distance && selectedActivity.moving_time) {
                        const distanceKm = selectedActivity.distance / 1000;
                        const movingTimeMin = selectedActivity.moving_time / 60;
                        const avgPace = movingTimeMin / distanceKm;
                        const paceMin = Math.floor(avgPace);
                        const paceSec = Math.round((avgPace - paceMin) * 60);

                        return (
                          <div style={{textAlign: 'center', padding: '40px'}}>
                            <div style={{fontSize: '48px', fontWeight: 'bold', color: '#14b8a6', marginBottom: '8px'}}>
                              {paceMin}:{paceSec.toString().padStart(2, '0')}
                            </div>
                            <div style={{fontSize: '14px', color: '#666'}}>
                              Average Pace (min/km)
                            </div>
                            <div style={{fontSize: '12px', color: '#999', marginTop: '16px'}}>
                              Detailed split data not available for this activity
                            </div>
                            {selectedActivity.activity_id && (
                              <button
                                onClick={() => handleFetchSplits(selectedActivity.id)}
                                disabled={loadingSplits}
                                style={{
                                  marginTop: '16px',
                                  padding: '8px 16px',
                                  backgroundColor: loadingSplits ? '#ccc' : '#14b8a6',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: loadingSplits ? 'not-allowed' : 'pointer',
                                  fontSize: '13px'
                                }}
                              >
                                {loadingSplits ? 'Loading...' : 'Load Pace Splits'}
                              </button>
                            )}
                          </div>
                        );
                      }
                      return <p style={{textAlign: 'center', color: '#666', padding: '40px'}}>No pace data available for this activity</p>;
                    }

                    // Calculate pace data from real splits_metric
                    // Each split has: distance (m), moving_time (s), average_speed (m/s)
                    const paceData = selectedActivity.splits_metric.map(split => {
                      if (!split.moving_time || !split.distance) return null;
                      // Calculate pace in min/km
                      const kmDistance = split.distance / 1000;
                      const minutes = split.moving_time / 60;
                      return minutes / kmDistance;
                    }).filter(pace => pace !== null && pace > 0 && pace < 20); // Filter invalid values

                    if (paceData.length === 0) {
                      return <p style={{textAlign: 'center', color: '#666', padding: '40px'}}>No valid split data for pace chart</p>;
                    }

                    // Calculate chart dimensions
                    const chartWidth = 380;
                    const chartHeight = 150;
                    const padding = {left: 50, right: 20, top: 10, bottom: 30};
                    const plotWidth = chartWidth - padding.left - padding.right;
                    const plotHeight = chartHeight - padding.top - padding.bottom;

                    const minPace = Math.min(...paceData);
                    const maxPace = Math.max(...paceData);
                    const paceRange = maxPace - minPace || 1;

                    // Generate points
                    const points = paceData.map((pace, i) => {
                      const x = padding.left + (i / (paceData.length - 1 || 1)) * plotWidth;
                      const y = padding.top + plotHeight - ((pace - minPace) / paceRange) * plotHeight;
                      return {x, y, pace, km: i + 1};
                    });

                    const polylinePoints = points.map(p => `${p.x},${p.y}`).join(' ');

                    return (
                      <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
                        {/* Y-axis */}
                        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={chartHeight - padding.bottom} stroke="#ccc" strokeWidth="1"/>
                        {/* X-axis */}
                        <line x1={padding.left} y1={chartHeight - padding.bottom} x2={chartWidth - padding.right} y2={chartHeight - padding.bottom} stroke="#666" strokeWidth="1"/>

                        {/* Grid lines */}
                        <line x1={padding.left} y1={padding.top + plotHeight * 0.33} x2={chartWidth - padding.right} y2={padding.top + plotHeight * 0.33} stroke="#eee" strokeWidth="1" strokeDasharray="4"/>
                        <line x1={padding.left} y1={padding.top + plotHeight * 0.67} x2={chartWidth - padding.right} y2={padding.top + plotHeight * 0.67} stroke="#eee" strokeWidth="1" strokeDasharray="4"/>

                        {/* Pace line */}
                        {points.length > 1 && (
                          <>
                            <polyline points={polylinePoints} fill="none" stroke="#14b8a6" strokeWidth="2"/>
                            {points.map((point, i) => (
                              <circle key={i} cx={point.x} cy={point.y} r="3" fill="#14b8a6" stroke="#fff" strokeWidth="1"/>
                            ))}
                          </>
                        )}

                        {/* X-axis labels (km) */}
                        {points.map((point, i) => (
                          <text key={`x-${i}`} x={point.x} y={chartHeight - padding.bottom + 15} fontSize="12" fill="#666" textAnchor="middle">
                            {point.km}
                          </text>
                        ))}

                        {/* Y-axis labels (pace) */}
                        <text x={padding.left - 5} y={padding.top + 5} fontSize="10" fill="#666" textAnchor="end">
                          {Math.floor(maxPace)}:{Math.round((maxPace % 1) * 60).toString().padStart(2, '0')}
                        </text>
                        <text x={padding.left - 5} y={chartHeight - padding.bottom + 5} fontSize="10" fill="#666" textAnchor="end">
                          {Math.floor(minPace)}:{Math.round((minPace % 1) * 60).toString().padStart(2, '0')}
                        </text>
                      </svg>
                    );
                  })()}
                </div>
              </div>

              {/* Map Preview Card */}
              <div
                className="map-preview-card"
                onClick={() => selectedActivity.summary_polyline && setShowMapModal(true)}
                style={{ cursor: selectedActivity.summary_polyline ? 'pointer' : 'default' }}
              >
                {selectedActivity.summary_polyline ? (
                  <RouteMap summaryPolyline={selectedActivity.summary_polyline} height="300px" />
                ) : (
                  <>
                    <div className="map-preview">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <path d="M16 4C11 4 7 8 7 13c0 7 9 15 9 15s9-8 9-15c0-5-4-9-9-9z" stroke="#717182" strokeWidth="2"/>
                        <circle cx="16" cy="13" r="3" fill="#717182"/>
                      </svg>
                    </div>
                    <p className="map-preview-text">No route data available</p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
      <ActivityImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        email={userEmail}
        onSuccess={handleImportComplete}
        onError={handleModalError}
      />
      <ManualActivityForm
        isOpen={showManualModal}
        onClose={() => setShowManualModal(false)}
        email={userEmail}
        onSuccess={handleManualComplete}
        onError={handleModalError}
      />
      {showMapModal && selectedActivity && (
        <MapModal
          summaryPolyline={selectedActivity.summary_polyline}
          activityName={selectedActivity.name}
          onClose={() => setShowMapModal(false)}
        />
      )}
    </div>
  );
};

export default Activities;
